import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Truck, Shield, Star, Plus, Minus, ShoppingCart, Heart, MessageCircle, Send, ChevronDown, Store, Users, Package } from 'lucide-react';
import { productsAPI, questionsAPI, adsAPI } from '../services/api';
import { getImageUrl, formatPrice } from '../utils/helpers';
import ProductCard from '../components/ProductCard';
import { useFavorites } from '../context/FavoritesContext';
import { trackViewContent, trackAddToCart } from '../utils/analytics';
import { VariantSelector } from '../components/VariantSelector';
import { DeliveryPromise } from '../components/product/DeliveryPromise';
import { PaymentMethods } from '../components/product/PaymentMethods';
import { CustomerReviews } from '../components/reviews/CustomerReviews';
import { RatingSummary } from '../components/reviews/RatingSummary';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';

/**
 * Time ago helper
 */
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'hace un momento';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return 'hace 1 dia';
  if (days < 30) return `hace ${days} dias`;
  if (days < 365) return `hace ${Math.floor(days / 30)} meses`;
  return `hace ${Math.floor(days / 365)} anos`;
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [product, setProduct] = useState(null);
  const [productError, setProductError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [question, setQuestion] = useState('');
  const [productQuestions, setProductQuestions] = useState([]);
  const [sponsoredAd, setSponsoredAd] = useState(null);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [searchParams] = useSearchParams();

  // Scroll to top when navigating between products
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    let active = true;
    setProduct(null);
    setProductError(false);
    loadProduct(() => active);
    return () => { active = false; };
  }, [id]);

  const loadProduct = async (isCurrent = () => true) => {
    try {
      const foundProduct = await productsAPI.getById(id);
      if (!isCurrent()) return;
      setProduct(foundProduct);
      setSelectedImage(0);
      setQuantity(1);
      const urlSku = searchParams.get('variant');
      setSelectedVariant((foundProduct.variants || []).find(v => v.sku && v.sku === urlSku) || null);
      trackViewContent(foundProduct);

      // Load questions for this product
      const questionsData = await questionsAPI.getByProduct(foundProduct._id);
      if (!isCurrent()) return;
      setProductQuestions(questionsData);

      // Get related products from same category
      const allProducts = await productsAPI.getAll({ category: foundProduct.category });
      if (!isCurrent()) return;
      const related = allProducts.filter(p => (p._id || p.id) !== foundProduct._id).slice(0, 8);
      setRelatedProducts(related);

      // Load a sponsored ad for the carousel
      loadSponsoredAd(foundProduct.category, foundProduct._id);
    } catch (error) {
      console.error('Error loading product:', error);
      if (isCurrent()) setProductError(true);
    }
  };

  const loadSponsoredAd = async (category, currentProductId) => {
    try {
      const ads = await adsAPI.getAdsForFeed({ category, limit: 3 });
      const ad = ads.find(a => (a._id || a.id) !== currentProductId);
      if (ad) {
        setSponsoredAd(ad);
        if (ad.campaign_id) adsAPI.recordImpression(ad.campaign_id).catch(() => {});
      }
    } catch {
      setSponsoredAd(null);
    }
  };

  const handleAdClick = (campaignId) => {
    if (campaignId) adsAPI.recordClick(campaignId).catch(() => {});
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Helmet><title>{productError ? 'Producto no disponible - Zona Libre' : 'Cargando producto - Zona Libre'}</title></Helmet>
        <p className="text-xl" data-testid="product-loading">{productError ? 'No se pudo cargar el producto.' : 'Cargando producto…'}</p>
      </div>
    );
  }

  const fmtPrice = (price) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
    }).format(price);

  const hasVariants = (product.variants || []).length > 0;
  const displayPrice = selectedVariant ? Number(selectedVariant.price) : product.price;
  const displayStock = selectedVariant ? Number(selectedVariant.stock) : product.stock;

  const requireVariant = () => {
    if (hasVariants && !selectedVariant) {
      toast({ title: 'Elige una variante', description: 'Selecciona talla y/o color antes de continuar', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const handleSelectVariant = (v) => {
    setSelectedVariant(v);
    setQuantity(1);
  };

  const handleAddToCart = () => {
    if (displayStock < 1 || !requireVariant()) return;
    addToCart(product, quantity, selectedVariant);
    trackAddToCart({ ...product, price: displayPrice }, quantity);
    toast({
      title: '¡Producto agregado!',
      description: `${product.title}${selectedVariant ? ` (${[selectedVariant.size, selectedVariant.color].filter(Boolean).join(' / ')})` : ''} se agregó al carrito`,
    });
  };

  const handleBuyNow = () => {
    if (!requireVariant()) return;
    addToCart(product, quantity, selectedVariant);
    trackAddToCart({ ...product, price: displayPrice }, quantity);
    navigate('/checkout');
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }
    if (!question.trim()) return;

    try {
      await questionsAPI.create(id, question.trim());
      setQuestion('');
      await loadProduct();
      toast({
        title: '¡Pregunta enviada!',
        description: 'El vendedor responderá pronto',
      });
    } catch (error) {
      console.error('Error submitting question:', error);
      toast({
        title: 'Error',
        description: 'No se pudo enviar la pregunta',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Helmet>
        <title>{`${product.title} - Zona Libre`}</title>
        <meta name="description" content={product.description ? product.description.substring(0, 160) : product.title} />
        <meta property="og:title" content={`${product.title} - Zona Libre`} />
        <meta property="og:description" content={product.description ? product.description.substring(0, 160) : product.title} />
        {product.images?.[0] && <meta property="og:image" content={getImageUrl(product.images[0])} />}
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="text-sm text-gray-600 mb-4">
          <span className="hover:text-zl-orange cursor-pointer" onClick={() => navigate('/')}>
            Inicio
          </span>
          {' > '}
          <span className="hover:text-zl-orange cursor-pointer" onClick={() => navigate(`/category/${product.category}`)}>
            {product.category}
          </span>
          {' > '}
          <span className="text-gray-900 hidden sm:inline">{product.title}</span>
        </div>

        {/* === MAIN LAYOUT === */}
        {/* Mobile: flex-col with order; Desktop: 3-col grid */}
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-3 lg:gap-6">

          {/* ---- MOBILE-ONLY Product Header (condition, sales, title, rating) ---- */}
          <div className="order-1 lg:hidden">
            <p className="text-sm text-gray-500 mb-1" data-testid="product-condition-sales-mobile">
              {product.condition || 'Nuevo'} | +{product.sales_count || 0} vendidos
            </p>
            <h1 className="text-xl font-bold mb-1" data-testid="product-title-mobile">{product.title}</h1>
            <RatingSummary average={product.averageRating} count={product.numReviews} testId="product-rating-mobile" />
          </div>

          {/* ---- IMAGE SECTION ---- */}
          <div className="order-2 lg:order-1 lg:col-span-2">
            <Card className="p-4 lg:p-6">
              <div className="relative mb-4">
                <img
                  src={getImageUrl(product.images?.[selectedImage] || product.images?.[0])}
                  alt={product.title}
                  className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-contain rounded-lg"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/500x500?text=Imagen+no+disponible';
                  }}
                />
                {product.discount > 0 && (
                  <Badge className="absolute top-4 left-4 bg-green-500 text-lg px-4 py-2">
                    {product.discount}% OFF
                  </Badge>
                )}
              </div>

              {/* Image thumbnails */}
              {product.images && product.images.length > 1 && (
                <div className="flex gap-2 lg:gap-3 overflow-x-auto pb-2">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-16 h-16 lg:w-20 lg:h-20 rounded border-2 overflow-hidden transition-all flex-shrink-0 ${
                        selectedImage === idx
                          ? 'border-zl-orange scale-105'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${product.title} ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/80x80?text=No+img';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* ---- RIGHT COLUMN WRAPPER ----
              Mobile: display:contents → children float as independent flex items
              Desktop: display:block → children stack inside a single grid cell with sticky + z-20 */}
          <div className="contents lg:block lg:order-2 lg:row-span-3 lg:relative lg:z-20 lg:space-y-4">

            {/* PURCHASE CARD */}
            <div className="order-3">
              <Card className="p-5 lg:p-6 lg:sticky lg:top-24">
                {/* Desktop-only header (mobile shows it above the image) */}
                <div className="hidden lg:block mb-4">
                  <p className="text-sm text-gray-500 mb-1" data-testid="product-condition-sales">
                    {product.condition || 'Nuevo'} | +{product.sales_count || 0} vendidos
                  </p>
                  <h1 className="text-2xl font-bold mb-2" data-testid="product-title">{product.title}</h1>
                  <RatingSummary average={product.averageRating} count={product.numReviews} testId="product-rating-desktop" />
                </div>

                {product.originalPrice && (
                  <p className="text-gray-400 line-through text-lg">{fmtPrice(product.originalPrice)}</p>
                )}
                <div className="text-3xl lg:text-4xl font-bold mb-2" data-testid="product-price">
                  {hasVariants && !selectedVariant ? `Desde ${fmtPrice(product.price)}` : fmtPrice(displayPrice)}
                </div>

                {hasVariants && (
                  <VariantSelector variants={product.variants} selected={selectedVariant} onSelect={handleSelectVariant} />
                )}

                <DeliveryPromise product={product} />

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Vendido por:{' '}
                    <span
                      className="font-semibold text-zl-orange hover:underline cursor-pointer"
                      onClick={() => navigate(`/store/${product.seller_id}`)}
                    >
                      {product.seller}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Stock disponible: <span className="font-semibold" data-testid="product-stock">{displayStock} unidades</span>
                  </p>
                </div>

                {/* Quantity selector */}
                <div className="mb-6">
                  <label className="text-sm font-medium mb-2 block">Cantidad:</label>
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-xl font-semibold w-12 text-center">{quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.min(displayStock, quantity + 1))}
                      disabled={quantity >= displayStock}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    className="w-full bg-zl-orange hover:bg-zl-orange-600 text-white text-lg h-12"
                    onClick={handleBuyNow}
                    disabled={displayStock < 1}
                    data-testid="buy-now-btn"
                  >
                    Comprar ahora
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-zl-orange text-zl-orange hover:bg-zl-orange-50 text-lg h-12"
                    onClick={handleAddToCart}
                    disabled={displayStock < 1}
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Agregar al carrito
                  </Button>
                  <Button variant="ghost" className="w-full"
                    onClick={() => {
                      if (!user) { navigate('/login'); return; }
                      toggleFavorite(product._id || product.id);
                    }}
                    data-testid="toggle-favorite-btn"
                  >
                    <Heart className={`w-5 h-5 mr-2 ${isFavorite(product._id || product.id) ? 'fill-red-500 text-red-500' : ''}`} />
                    {isFavorite(product._id || product.id) ? 'Guardado en favoritos' : 'Agregar a favoritos'}
                  </Button>
                </div>

                <PaymentMethods />

                {/* Trust badges */}
                <div className="mt-6 pt-6 border-t space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <Shield className="w-5 h-5 mr-2 text-zl-orange" />
                    Compra Protegida
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Truck className="w-5 h-5 mr-2 text-zl-orange" />
                    Devolución gratis
                  </div>
                </div>
              </Card>
            </div>

            {/* STORE CARD */}
            <div className="order-5">
              <Card className="overflow-hidden" data-testid="store-card">
                <div className="h-20 bg-gradient-to-r from-zl-orange to-amber-400 relative">
                  <div className="absolute -bottom-8 left-4">
                    <div className="w-16 h-16 rounded-full border-4 border-white bg-white overflow-hidden shadow-md">
                      <img
                        src={`https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(product.seller || 'T')}`}
                        alt={product.seller}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
                <div className="pt-10 px-4 pb-4">
                  <h3 className="font-bold text-lg" data-testid="store-name">{product.seller || 'Tienda'}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    <span data-testid="store-followers">
                      <Users className="w-4 h-4 inline mr-1" />
                      {product.store_followers ?? 128} seguidores
                    </span>
                    <span data-testid="store-products">
                      <Package className="w-4 h-4 inline mr-1" />
                      {product.store_product_count ?? 24} productos
                    </span>
                  </div>
                  <div className="flex items-center mt-2 text-sm" data-testid="store-reputation">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
                    <span className="font-semibold">{product.store_reputation ?? 4.8}</span>
                    <span className="text-gray-500 ml-1">reputacion</span>
                  </div>
                  <Button
                    className="w-full mt-4 bg-zl-orange hover:bg-zl-orange-600 text-white"
                    onClick={() => navigate(`/store/${product.seller_id}`)}
                    data-testid="go-to-store-btn"
                  >
                    <Store className="w-4 h-4 mr-2" />
                    Ir a la tienda
                  </Button>
                </div>
              </Card>
            </div>

          </div>{/* end right column wrapper */}

          {/* ---- DESCRIPTION / FEATURES ---- */}
          <div className="order-4 lg:order-3 lg:col-span-2">
            <Card className="p-4 lg:p-6">
              <h2 className="text-xl lg:text-2xl font-bold mb-4">Descripcion</h2>
              <div className="relative">
                <p className={`text-gray-700 mb-6 ${!isDescExpanded && product.description?.length > 150 ? 'line-clamp-3' : ''}`}>
                  {product.description}
                </p>
                {product.description?.length > 150 && (
                  <button
                    onClick={() => setIsDescExpanded(!isDescExpanded)}
                    className="text-zl-orange hover:underline font-medium text-sm"
                    data-testid="desc-toggle-btn"
                  >
                    {isDescExpanded ? 'Ver menos' : 'Leer mas...'}
                  </button>
                )}
              </div>

              <h3 className="text-lg lg:text-xl font-bold mb-3">Características principales</h3>
              <ul className="space-y-2">
                {product.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-zl-orange mr-2">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* ---- QUESTIONS & ANSWERS ---- */}
          <div className="order-6 lg:order-4 lg:col-span-2">
            <Card className="p-4 lg:p-6">
              <div className="flex items-center mb-6">
                <MessageCircle className="w-6 h-6 mr-2 text-zl-orange" />
                <h2 className="text-xl lg:text-2xl font-bold">Preguntas y respuestas</h2>
              </div>

              {/* Ask Question Form */}
              {user && (
                <Card className="p-4 mb-6 bg-zl-orange-50">
                  <form onSubmit={handleSubmitQuestion}>
                    <div className="mb-3">
                      <Textarea
                        placeholder="Hacele una pregunta al vendedor..."
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        rows={3}
                        className="bg-white"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={!question.trim()}
                      className="bg-zl-orange hover:bg-zl-orange-600"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Enviar pregunta
                    </Button>
                  </form>
                </Card>
              )}

              {!user && (
                <Card className="p-6 mb-6 text-center bg-gray-50">
                  <p className="text-gray-600 mb-3">Inicia sesión para hacer una pregunta</p>
                  <Button onClick={() => navigate('/login')} className="bg-zl-orange hover:bg-zl-orange-600">
                    Iniciar sesión
                  </Button>
                </Card>
              )}

              {/* Questions List */}
              <div className="space-y-4">
                {productQuestions.length === 0 ? (
                  <Card className="p-8 text-center">
                    <MessageCircle className="w-16 h-16 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Todavia no hay preguntas. Se el primero en preguntar!</p>
                  </Card>
                ) : (
                  <>
                    {(showAllQuestions ? productQuestions : productQuestions.slice(0, 2)).map((q) => {
                      const rawDate = q.created_at || q.date || q.timestamp;
                      const dateObj = rawDate ? new Date(rawDate) : null;
                      const formattedDate = dateObj ? dateObj.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                      const ago = rawDate ? timeAgo(rawDate) : '';

                      return (
                        <Card key={q.id || q._id} className="p-4">
                          <div className="mb-3">
                            <div className="flex items-start">
                              <MessageCircle className="w-5 h-5 text-gray-400 mr-2 mt-1 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{q.question}</p>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                                  <span>{q.userName || 'Usuario'}</span>
                                  {formattedDate && <><span className="text-gray-300">|</span><span>{formattedDate}</span></>}
                                  {ago && <span className="text-xs text-gray-400">({ago})</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                          {q.answered ? (
                            <div className="ml-7 pl-4 border-l-2 border-green-500">
                              <p className="text-gray-700">{q.answer}</p>
                              <p className="text-sm text-gray-500 mt-1">Respuesta del vendedor</p>
                            </div>
                          ) : (
                            <div className="ml-7 pl-4 border-l-2 border-yellow-500">
                              <p className="text-sm text-yellow-600 font-medium">Esperando respuesta del vendedor</p>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                    {productQuestions.length > 2 && !showAllQuestions && (
                      <Button variant="outline" className="w-full border-gray-300 text-gray-600 hover:bg-gray-50" onClick={() => setShowAllQuestions(true)} data-testid="show-more-questions">
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Ver las {productQuestions.length - 2} preguntas restantes
                      </Button>
                    )}
                    {showAllQuestions && productQuestions.length > 2 && (
                      <Button variant="ghost" className="w-full text-gray-500" onClick={() => setShowAllQuestions(false)}>
                        Mostrar menos
                      </Button>
                    )}
                  </>
                )}
              </div>
            </Card>
          </div>

          <div className="order-7 lg:order-5 lg:col-span-2 min-w-0">
            <CustomerReviews key={product._id} product={product} />
          </div>
        </div>{/* end main grid */}

        {/* Related Products Carousel */}
        {(relatedProducts.length > 0 || sponsoredAd) && (
          <div className="mt-8 lg:mt-12 mb-8" data-testid="related-products-carousel">
            <h2 className="text-xl lg:text-2xl font-bold mb-6">Productos relacionados</h2>
            <div className="px-6 lg:px-12">
              <Carousel opts={{ align: 'start', loop: relatedProducts.length > 3 }}>
                <CarouselContent>
                  {sponsoredAd && (
                    <CarouselItem className="basis-[70%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4" data-testid="carousel-sponsored-slot">
                      <div className="relative h-full ring-2 ring-amber-300 rounded-xl overflow-hidden">
                        <ProductCard product={sponsoredAd} onAdClick={handleAdClick} />
                      </div>
                    </CarouselItem>
                  )}
                  {relatedProducts.map(p => (
                    <CarouselItem key={p._id || p.id} className="basis-[70%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                      <ProductCard product={p} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
